import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, TrendingUp, TrendingDown, AlertTriangle, Truck, 
  DollarSign, Wrench, RefreshCw, ChevronRight, Minus
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MaintenancePredictorPage() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [predictions, setPredictions] = useState(null);
  const [trends, setTrends] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleDetail, setVehicleDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [predictionsRes, trendsRes] = await Promise.all([
        axios.get(`${API}/api/maintenance/predictions`, { headers }),
        axios.get(`${API}/api/maintenance/trends`, { headers })
      ]);

      setPredictions(predictionsRes.data);
      setTrends(trendsRes.data);
    } catch (err) {
      console.error('Failed to fetch maintenance data:', err);
      toast.error('Failed to load maintenance predictions');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      await axios.post(`${API}/api/maintenance/refresh-cache`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Data refreshed');
      await fetchData();
    } catch (err) {
      toast.error('Failed to refresh');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchVehicleDetail = async (vehicleName) => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const res = await axios.get(`${API}/api/maintenance/vehicle/${encodeURIComponent(vehicleName)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicleDetail(res.data);
      setSelectedVehicle(vehicleName);
    } catch (err) {
      toast.error('Failed to load vehicle details');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getTrendIcon = (direction) => {
    if (direction === 'up') return <TrendingUp className="h-4 w-4 text-red-400" />;
    if (direction === 'down') return <TrendingDown className="h-4 w-4 text-green-400" />;
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="maintenance-predictor-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/parts">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Wrench className="h-6 w-6 text-orange-500" />
              Maintenance Cost Predictor
            </h1>
            <p className="text-muted-foreground">Predict and analyze truck maintenance costs</p>
          </div>
        </div>
        <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {predictions?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-blue-500/20">
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Trucks Analyzed</div>
              <div className="text-2xl font-bold text-blue-400">
                {predictions.summary.total_trucks_analyzed}
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-500/20">
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Predicted Next Month</div>
              <div className="text-2xl font-bold text-green-400">
                {formatCurrency(predictions.summary.total_predicted_next_month)}
              </div>
            </CardContent>
          </Card>
          <Card className="border-purple-500/20">
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Avg Monthly/Truck</div>
              <div className="text-2xl font-bold text-purple-400">
                {formatCurrency(predictions.summary.avg_monthly_fleet_cost)}
              </div>
            </CardContent>
          </Card>
          <Card className={`${predictions.summary.vehicles_flagged_for_repairs > 0 ? 'border-red-500/50 bg-red-500/5' : 'border-gray-500/20'}`}>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Flagged for Repairs</div>
              <div className={`text-2xl font-bold flex items-center gap-2 ${predictions.summary.vehicles_flagged_for_repairs > 0 ? 'text-red-400' : 'text-gray-400'}`}>
                {predictions.summary.vehicles_flagged_for_repairs}
                {predictions.summary.vehicles_flagged_for_repairs > 0 && <AlertTriangle className="h-5 w-5" />}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="flagged">Flagged Vehicles</TabsTrigger>
          <TabsTrigger value="predictions">All Predictions</TabsTrigger>
          <TabsTrigger value="trends">Fleet Trends</TabsTrigger>
          {selectedVehicle && <TabsTrigger value="vehicle">Vehicle Detail</TabsTrigger>}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Monthly Trend Chart */}
          {trends?.monthly_trend && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-400" />
                  Monthly Maintenance Costs (Trucks Only)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-6 gap-2">
                  {trends.monthly_trend.slice(0, 6).map((month) => (
                    <div key={month.month} className="text-center p-3 rounded-lg bg-secondary/30">
                      <p className="text-xs text-muted-foreground">{month.month}</p>
                      <p className="text-lg font-bold text-green-400">{formatCurrency(month.total)}</p>
                      <p className="text-xs text-muted-foreground">{month.service_count} services</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Predicted Costs */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-400" />
                Top 10 Highest Predicted Costs (Next Month)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {predictions?.predictions?.slice(0, 10).map((pred, idx) => (
                  <div 
                    key={pred.vehicle_name}
                    className="flex justify-between items-center p-3 rounded-lg bg-secondary/30 cursor-pointer hover:bg-secondary/50 transition-colors"
                    onClick={() => fetchVehicleDetail(pred.vehicle_name)}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        idx < 3 ? 'bg-orange-500 text-white' : 'bg-secondary text-muted-foreground'
                      }`}>
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          <Truck className="h-4 w-4" />
                          {pred.vehicle_name}
                          {pred.flag_major_repair && (
                            <AlertTriangle className="h-4 w-4 text-red-400" />
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {pred.vehicle_type} • {pred.service_count} services (12m)
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-green-400 font-bold">{formatCurrency(pred.predicted_next_month)}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                          {getTrendIcon(pred.trend_direction)}
                          {pred.trend_percent > 0 ? '+' : ''}{pred.trend_percent}%
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Flagged Vehicles Tab */}
        <TabsContent value="flagged">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="h-5 w-5" />
                Vehicles Flagged for Potential Major Repairs
              </CardTitle>
            </CardHeader>
            <CardContent>
              {predictions?.flagged_vehicles?.length > 0 ? (
                <div className="space-y-3">
                  {predictions.flagged_vehicles.map((pred) => (
                    <div 
                      key={pred.vehicle_name}
                      className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 cursor-pointer hover:bg-red-500/20 transition-colors"
                      onClick={() => fetchVehicleDetail(pred.vehicle_name)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-bold text-lg flex items-center gap-2">
                            <Truck className="h-5 w-5" />
                            {pred.vehicle_name}
                          </p>
                          <p className="text-sm text-muted-foreground">{pred.vehicle_type}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-red-400 font-bold text-lg">{formatCurrency(pred.predicted_next_month)}</p>
                          <p className="text-sm text-muted-foreground">predicted next month</p>
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">12m Total</p>
                          <p className="font-medium">{formatCurrency(pred.total_cost_12m)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Avg Monthly</p>
                          <p className="font-medium">{formatCurrency(pred.avg_monthly_cost)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Trend</p>
                          <p className={`font-medium flex items-center gap-1 ${pred.trend_percent > 0 ? 'text-red-400' : 'text-green-400'}`}>
                            {getTrendIcon(pred.trend_direction)}
                            {pred.trend_percent > 0 ? '+' : ''}{pred.trend_percent}%
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No vehicles currently flagged for major repairs
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Predictions Tab */}
        <TabsContent value="predictions">
          <Card>
            <CardHeader>
              <CardTitle>All Truck Predictions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="text-left p-3 text-sm">Vehicle</th>
                      <th className="text-left p-3 text-sm">Type</th>
                      <th className="text-right p-3 text-sm">12m Total</th>
                      <th className="text-right p-3 text-sm">Avg Monthly</th>
                      <th className="text-right p-3 text-sm">Predicted</th>
                      <th className="text-center p-3 text-sm">Trend</th>
                      <th className="text-center p-3 text-sm">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {predictions?.all_predictions?.map((pred) => (
                      <tr 
                        key={pred.vehicle_name}
                        className="border-b border-border/50 hover:bg-secondary/30 cursor-pointer"
                        onClick={() => fetchVehicleDetail(pred.vehicle_name)}
                      >
                        <td className="p-3 font-medium">{pred.vehicle_name}</td>
                        <td className="p-3 text-muted-foreground text-sm">{pred.vehicle_type}</td>
                        <td className="p-3 text-right">{formatCurrency(pred.total_cost_12m)}</td>
                        <td className="p-3 text-right">{formatCurrency(pred.avg_monthly_cost)}</td>
                        <td className="p-3 text-right font-bold text-green-400">{formatCurrency(pred.predicted_next_month)}</td>
                        <td className="p-3 text-center">
                          <span className={`flex items-center justify-center gap-1 ${
                            pred.trend_percent > 10 ? 'text-red-400' : 
                            pred.trend_percent < -10 ? 'text-green-400' : 'text-gray-400'
                          }`}>
                            {getTrendIcon(pred.trend_direction)}
                            {pred.trend_percent > 0 ? '+' : ''}{pred.trend_percent}%
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          {pred.flag_major_repair && (
                            <AlertTriangle className="h-4 w-4 text-red-400 mx-auto" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fleet Trends Tab */}
        <TabsContent value="trends" className="space-y-6">
          {trends && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Total 12m Cost</div>
                    <div className="text-2xl font-bold text-green-400">{formatCurrency(trends.total_12m)}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Avg Monthly</div>
                    <div className="text-2xl font-bold text-blue-400">{formatCurrency(trends.avg_monthly)}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Total Services</div>
                    <div className="text-2xl font-bold text-purple-400">{trends.total_services}</div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Costs by Vehicle Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {trends.by_vehicle_type?.map((type) => (
                      <div key={type.type} className="flex justify-between items-center p-3 rounded-lg bg-secondary/30">
                        <span className="font-medium">{type.type}</span>
                        <span className="text-green-400 font-bold">{formatCurrency(type.total_cost)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Vehicle Detail Tab */}
        <TabsContent value="vehicle">
          {vehicleDetail && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="h-5 w-5" />
                    {vehicleDetail.vehicle?.name} - {vehicleDetail.vehicle?.type}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Status</p>
                      <p className="font-medium">{vehicleDetail.vehicle?.status}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Meter</p>
                      <p className="font-medium">{vehicleDetail.vehicle?.meter?.toLocaleString()} {vehicleDetail.vehicle?.meter_unit}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">12m Total Cost</p>
                      <p className="font-bold text-green-400">{formatCurrency(vehicleDetail.total_cost_12m)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Services</p>
                      <p className="font-medium">{vehicleDetail.total_services}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Monthly Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {vehicleDetail.monthly_trend?.map((month) => (
                        <div key={month.month} className="flex justify-between items-center p-2 rounded bg-secondary/30">
                          <span className="text-sm">{month.month}</span>
                          <span className="font-medium text-green-400">{formatCurrency(month.cost)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Top Parts Used</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {vehicleDetail.top_parts?.slice(0, 10).map((part, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 rounded bg-secondary/30">
                          <span className="text-sm truncate flex-1 mr-2">{part.part}</span>
                          <span className="font-medium text-green-400">{formatCurrency(part.total_cost)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Services</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {vehicleDetail.recent_services?.map((service, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 rounded-lg bg-secondary/30">
                        <div>
                          <p className="font-medium">{service.date}</p>
                          <p className="text-xs text-muted-foreground">
                            WO #{service.work_order} • Parts: {formatCurrency(service.parts)} • Labor: {formatCurrency(service.labor)}
                          </p>
                        </div>
                        <span className="font-bold text-green-400">{formatCurrency(service.total)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
